import {FluxErrorCodes} from "@buildwithflux/constants";
import {FluxBaseError} from "@buildwithflux/core";
import {IDocumentData} from "@buildwithflux/models";
import {areWeTestingWithJest, isDevEnv} from "@buildwithflux/shared";
import type {FirestoreErrorCode} from "firebase/firestore";
import {OptionsObject, SnackbarKey, SnackbarMessage} from "notistack";
import {StoreApi, UseBoundStore, create} from "zustand";
import {devtools} from "zustand/middleware";

import {useFluxServices} from "injection/hooks";

/**
 * Application status enum - tracks the overall state of the application
 */
export enum AppStatus {
    noDocumentData,
    loadingDocument,
    documentReady,
}

/**
 * Document status states - tracks the sync state of the current document
 */
export enum DocumentStatusStates {
    none,
    loaded,
    loading,
    pending,
    syncing,
    synced,
    error,
    syncNeeded,
}

/**
 * Document status state interface
 */
export interface IDocumentStatusState {
    state: DocumentStatusStates;
    timestamp: number;
    message?: string;
}

/**
 * Notification options type
 */
export type INotificationOptions = Omit<OptionsObject, "anchorOrigin"> & {
    anchorOrigin?: {vertical: "top"; horizontal: "left" | "center" | "right"};
};

/**
 * Notification interface
 */
export interface INotification {
    key: SnackbarKey;
    surfaceOrObject: string;
    message: SnackbarMessage;
    options: INotificationOptions;
    dismiss?: boolean;
}

/**
 * Storage error class for handling storage-related errors
 */
export class StorageError extends FluxBaseError {
    constructor(message: string, public readonly code?: FirestoreErrorCode, options: ErrorOptions = {}) {
        super(FluxErrorCodes.StorageError, message, options);
    }
}

/**
 * State for managing application-level state
 *
 * This replaces the Redux app reducer state, which tracked application status,
 * document status, notifications, and other app-level concerns.
 */
export type AppStoreState = {
    /**
     * Current application status
     */
    appStatus: AppStatus;

    /**
     * Current document status (sync state)
     */
    documentStatus: IDocumentStatusState;

    /**
     * Array of active notifications
     */
    notifications: INotification[];

    /**
     * Current error, if any
     */
    error?: StorageError;

    /**
     * Search key for the part browser
     */
    partBrowserSearchKey: string;

    /**
     * Whether to show the editor backdrop
     */
    showEditorBackdrop: boolean;

    /**
     * Last write document payload for tracking document writes
     */
    lastWriteDocumentPayload?: {
        documentState: IDocumentData;
    };

    /**
     * Whether the app is currently reconnecting
     */
    isReconnecting: boolean;

    /**
     * Whether the user is in onboarding mode
     */
    isOnboarding: boolean;

    /**
     * Sets the application error
     */
    setError: (error?: StorageError) => void;

    /**
     * Sets the application status
     */
    setAppStatus: (status: AppStatus) => void;

    /**
     * Sets the document status
     */
    setDocumentStatus: (state: DocumentStatusStates, timestamp: number, message?: string) => void;

    /**
     * Enqueues a notification
     */
    enqueueNotification: (message: SnackbarMessage, surfaceOrObject: string, options?: OptionsObject) => void;

    /**
     * Removes a notification by key
     */
    removeNotification: (key: SnackbarKey) => void;

    /**
     * Toggles the editor backdrop
     */
    toggleEditorBackdrop: (show: boolean) => void;

    /**
     * Sets the last write document payload
     */
    setLastWriteDocumentPayload: (documentState: IDocumentData) => void;

    /**
     * Sets the reconnecting state
     */
    setIsReconnecting: (reconnecting: boolean) => void;

    /**
     * Sets the onboarding state
     */
    setIsOnboarding: (onboarding: boolean) => void;

    /**
     * Clears document data (resets app status to noDocumentData)
     */
    clearDocumentData: () => void;
};

/**
 * The exported type of the store
 */
export type UseAppStore = UseBoundStore<StoreApi<AppStoreState>>;

/**
 * The exported type of the raw vanilla store (for non-React access like epics)
 */
export type AppStore = StoreApi<AppStoreState>;

/**
 * Registers the beforeunload handler based on document status
 */
function registerBeforeunload(state: DocumentStatusStates) {
    if (typeof window === "undefined") return;
    if (state === DocumentStatusStates.pending || state === DocumentStatusStates.syncing) {
        window.onbeforeunload = (event) => {
            event.preventDefault();
            event.returnValue = "Test";
        };
    } else {
        window.onbeforeunload = null;
    }
}

/**
 * Creates the vanilla non-bound-to-React app store
 *
 * This is a vanilla Zustand store, and is only exported for use in tests. In components, you'd usually use the hooks
 * below, and even when trying to access low-level details of the store, you'd use the bound version of this store.
 */
export function createAppStore(): UseAppStore {
    return create<AppStoreState>()(
        devtools(
            (set): AppStoreState => {
                return {
                    appStatus: AppStatus.noDocumentData,
                    documentStatus: {
                        state: DocumentStatusStates.none,
                        timestamp: new Date().getTime(),
                    },
                    notifications: [],
                    partBrowserSearchKey: "",
                    showEditorBackdrop: false,
                    isReconnecting: true,
                    isOnboarding: false,

                    setError: (error?: StorageError) => {
                        set(
                            {
                                appStatus: AppStatus.noDocumentData,
                                error,
                            },
                            false,
                            "setError",
                        );
                    },

                    setAppStatus: (status: AppStatus) => {
                        set({appStatus: status}, false, "setAppStatus");
                    },

                    setDocumentStatus: (state: DocumentStatusStates, timestamp: number, message?: string) => {
                        registerBeforeunload(state);
                        set(
                            {
                                documentStatus: {
                                    state,
                                    timestamp,
                                    message,
                                },
                            },
                            false,
                            "setDocumentStatus",
                        );
                    },

                    enqueueNotification: (
                        message: SnackbarMessage,
                        surfaceOrObject: string,
                        options?: OptionsObject,
                    ) => {
                        set(
                            (state) => ({
                                notifications: [
                                    ...state.notifications,
                                    {
                                        key: options?.key ?? new Date().getTime() + Math.random(),
                                        message,
                                        surfaceOrObject,
                                        options: options as INotificationOptions,
                                    },
                                ],
                            }),
                            false,
                            "enqueueNotification",
                        );
                    },

                    removeNotification: (key: SnackbarKey) => {
                        set(
                            (state) => ({
                                notifications: state.notifications.map((notification) => {
                                    if (notification.key !== key) return notification;
                                    return {
                                        ...notification,
                                        dismiss: true,
                                    };
                                }),
                            }),
                            false,
                            "removeNotification",
                        );
                    },

                    toggleEditorBackdrop: (show: boolean) => {
                        set({showEditorBackdrop: show}, false, "toggleEditorBackdrop");
                    },

                    setLastWriteDocumentPayload: (documentState: IDocumentData) => {
                        set(
                            {
                                lastWriteDocumentPayload: {
                                    documentState,
                                },
                            },
                            false,
                            "setLastWriteDocumentPayload",
                        );
                    },

                    setIsReconnecting: (reconnecting: boolean) => {
                        set({isReconnecting: reconnecting}, false, "setIsReconnecting");
                    },

                    setIsOnboarding: (onboarding: boolean) => {
                        set({isOnboarding: onboarding}, false, "setIsOnboarding");
                    },

                    clearDocumentData: () => {
                        set({appStatus: AppStatus.noDocumentData}, false, "clearDocumentData");
                    },
                };
            },
            {enabled: isDevEnv() && !areWeTestingWithJest(), name: "AppStore"},
        ),
    );
}

/**
 * Creates both the raw store and the bound hook from the same store instance
 *
 * This is used by the service container to expose both the raw store (for epics/thunks) and the hook (for React components)
 */
export function createAppStoreAndHook(): {store: AppStore; hook: UseAppStore} {
    const store = createAppStore();
    return {store, hook: store};
}

/**
 * Hook to check if the document is ready
 */
export function useIsDocumentReady(): boolean {
    return useFluxServices().useAppStore((state) => state.appStatus === AppStatus.documentReady);
}

/**
 * Hook to get the current app status
 */
export function useAppStatus(): AppStatus {
    return useFluxServices().useAppStore((state) => state.appStatus);
}

/**
 * Hook to get the document status
 */
export function useDocumentStatus(): IDocumentStatusState {
    return useFluxServices().useAppStore((state) => state.documentStatus);
}

/**
 * Hook to get the current error
 */
export function useAppError(): StorageError | undefined {
    return useFluxServices().useAppStore((state) => state.error);
}

/**
 * Hook to get the notifications array
 */
export function useNotifications(): INotification[] {
    return useFluxServices().useAppStore((state) => state.notifications);
}

/**
 * Hook to get the part browser search key
 */
export function usePartBrowserSearchKey(): string {
    return useFluxServices().useAppStore((state) => state.partBrowserSearchKey);
}

/**
 * Hook to check if the editor backdrop should be shown
 */
export function useShowEditorBackdrop(): boolean {
    return useFluxServices().useAppStore((state) => state.showEditorBackdrop);
}

/**
 * Hook to get the last write document payload
 */
export function useLastWriteDocumentPayload(): {documentState: IDocumentData} | undefined {
    return useFluxServices().useAppStore((state) => state.lastWriteDocumentPayload);
}

/**
 * Hook to check if the app is reconnecting
 */
export function useIsReconnecting(): boolean {
    return useFluxServices().useAppStore((state) => state.isReconnecting);
}

/**
 * Hook to check if the user is in onboarding mode
 */
export function useIsOnboarding(): boolean {
    return useFluxServices().useAppStore((state) => state.isOnboarding);
}

/**
 * Hook to get the setError function
 */
export function useSetError(): (error?: StorageError) => void {
    return useFluxServices().useAppStore((state) => state.setError);
}

/**
 * Hook to get the setAppStatus function
 */
export function useSetAppStatus(): (status: AppStatus) => void {
    return useFluxServices().useAppStore((state) => state.setAppStatus);
}

/**
 * Hook to get the setDocumentStatus function
 */
export function useSetDocumentStatus(): (state: DocumentStatusStates, timestamp: number, message?: string) => void {
    return useFluxServices().useAppStore((state) => state.setDocumentStatus);
}

/**
 * Hook to get the enqueueNotification function
 */
export function useEnqueueNotification(): (
    message: SnackbarMessage,
    surfaceOrObject: string,
    options?: OptionsObject,
) => void {
    return useFluxServices().useAppStore((state) => state.enqueueNotification);
}

/**
 * Hook to get the removeNotification function
 */
export function useRemoveNotification(): (key: SnackbarKey) => void {
    return useFluxServices().useAppStore((state) => state.removeNotification);
}

/**
 * Hook to get the toggleEditorBackdrop function
 */
export function useToggleEditorBackdrop(): (show: boolean) => void {
    return useFluxServices().useAppStore((state) => state.toggleEditorBackdrop);
}

/**
 * Hook to get the setIsReconnecting function
 */
export function useSetIsReconnecting(): (reconnecting: boolean) => void {
    return useFluxServices().useAppStore((state) => state.setIsReconnecting);
}

/**
 * Hook to get the setIsOnboarding function
 */
export function useSetIsOnboarding(): (onboarding: boolean) => void {
    return useFluxServices().useAppStore((state) => state.setIsOnboarding);
}
